import { Capacitor } from "@capacitor/core";

export const PRODUCTION_API_ORIGIN = "https://setthestandard.fit";
const NATIVE_SESSION_KEY = "the_standard_native_session";

function cleanOrigin(value: string | undefined): string {
  return (value ?? "").trim().replace(/\/+$/, "");
}

export function getApiOrigin(): string {
  const configured = cleanOrigin(import.meta.env.VITE_API_ORIGIN as string | undefined);
  if (configured) return configured;
  return Capacitor.isNativePlatform() ? PRODUCTION_API_ORIGIN : "";
}

export function apiUrl(path: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const origin = getApiOrigin();
  return origin ? `${origin}${normalizedPath}` : normalizedPath;
}

export function appOriginForServer(): string {
  return getApiOrigin() || window.location.origin;
}

// Route a stored (R2) image URL through our same-origin proxy so it can be
// drawn onto a <canvas> without tainting it. R2's public bucket doesn't send
// CORS headers, so a direct crossOrigin load fails — the proxy re-serves the
// bytes with Access-Control-Allow-Origin:*. Pass through anything that isn't a
// remote http(s) URL (blob:/data:/relative) untouched.
export function canvasSafeImageUrl(url: string | null | undefined): string {
  if (!url) return "";
  if (!/^https?:\/\//i.test(url)) return url;
  return apiUrl(`/api/uploads/img?url=${encodeURIComponent(url)}`);
}

export function getNativeSessionToken(): string {
  if (!Capacitor.isNativePlatform()) return "";
  try {
    return localStorage.getItem(NATIVE_SESSION_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setNativeSessionToken(token: string | null | undefined) {
  if (!Capacitor.isNativePlatform()) return;
  try {
    if (token) localStorage.setItem(NATIVE_SESSION_KEY, token);
    else localStorage.removeItem(NATIVE_SESSION_KEY);
  } catch {
    // Ignore private-storage failures; cookie auth still covers web/PWA.
  }
}

export function nativeAuthHeaders(): Record<string, string> {
  const token = getNativeSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
